import { calculateEventTop, calculateEventHeight } from "@/lib/calendarPositioning";

interface BusyBlock {
  id: string;
  startTime: Date;
  endTime: Date;
  source?: string;
}

interface ConflictEvent {
  id: string;
  startTime: Date;
  endTime: Date;
  highestConflictSeverity?: string; // "critical", "high", "medium", "low"
}

interface AvailabilityShadingProps {
  busyBlocks: BusyBlock[];
  conflictEvents?: ConflictEvent[]; // Events with potential conflicts
  staffWorkStart: string; // Format: "07:00"
  staffWorkEnd: string;   // Format: "15:00"
  dayStart?: number;      // Default: 6 (6am)
  dayEnd?: number;        // Default: 18 (6pm)
}

export function AvailabilityShading({ 
  busyBlocks, 
  conflictEvents = [],
  staffWorkStart, 
  staffWorkEnd,
  dayStart = 6,
  dayEnd = 18,
}: AvailabilityShadingProps) {
  // Parse work hours
  const workStartHour = parseInt(staffWorkStart.split(":")[0]);
  const workStartMinute = parseInt(staffWorkStart.split(":")[1] || "0");
  const workEndHour = parseInt(staffWorkEnd.split(":")[0]);
  const workEndMinute = parseInt(staffWorkEnd.split(":")[1] || "0");

  // Calculate positions for work hour boundaries (60px per hour)
  const beforeWorkHeight = ((workStartHour - dayStart) * 60) + workStartMinute;
  const afterWorkTop = ((workEndHour - dayStart) * 60) + workEndMinute;
  const totalDayHeight = (dayEnd - dayStart) * 60;

  return (
    <>
      {/* Layer 1: Outside Work Hours (Before) */}
      {beforeWorkHeight > 0 && (
        <div
          className="absolute left-0 right-0 bg-[var(--wd-gray-50)] opacity-80 pointer-events-none"
          style={{
            top: 0,
            height: `${beforeWorkHeight}px`,
          }}
          data-testid="availability-before-work"
        />
      )}

      {/* Layer 1: Outside Work Hours (After) */}
      {afterWorkTop < totalDayHeight && (
        <div
          className="absolute left-0 right-0 bg-[var(--wd-gray-50)] opacity-80 pointer-events-none"
          style={{
            top: `${afterWorkTop}px`,
            height: `${totalDayHeight - afterWorkTop}px`,
          }}
          data-testid="availability-after-work"
        />
      )}

      {/* Layer 2: Busy Time Blocks (Google/Outlook Events) */}
      {busyBlocks.map((block) => {
        const top = calculateEventTop(block.startTime, dayStart);
        const height = calculateEventHeight(block.startTime, block.endTime);

        return (
          <div
            key={block.id}
            className="absolute bg-muted/50 pointer-events-none rounded-sm border-l-2 border-muted-foreground/20"
            style={{
              top: `${top}px`,
              height: `${height}px`,
              left: "4px",
              right: "4px",
            }}
            title={`Busy: ${block.source || 'Calendar event'}`}
            data-testid={`busy-block-${block.id}`}
          />
        );
      })}

      {/* Layer 3: Critical Conflict Shading */}
      {conflictEvents
        .filter(event => 
          event.highestConflictSeverity && 
          event.highestConflictSeverity.toLowerCase() === "critical"
        )
        .map((event) => {
          const top = calculateEventTop(event.startTime, dayStart);
          const height = calculateEventHeight(event.startTime, event.endTime);

          return (
            <div
              key={`conflict-${event.id}`}
              className="absolute pointer-events-none"
              style={{
                top: `${top}px`,
                height: `${height}px`,
                left: "0px",
                right: "0px",
                backgroundColor: "rgba(239, 68, 68, 0.3)", // red-500 with opacity
                backgroundImage: "repeating-linear-gradient(45deg, rgba(239, 68, 68, 0.4) 0, rgba(239, 68, 68, 0.4) 5px, transparent 5px, transparent 10px)",
              }}
              title="Critical conflict detected"
              data-testid={`conflict-shading-${event.id}`}
            />
          );
        })}
    </>
  );
}
